import * as RadixDialog from "@radix-ui/react-dialog";
import { X } from "lucide-react";
import type { ReactNode } from "react";

import { useT } from "@/shared/i18n/useT";
import { cn } from "@/shared/lib/cn";
import { LAYER } from "@/shared/ui/styles";

export type DialogSize = "md" | "lg";

const WIDTH: Record<DialogSize, string> = {
  md: "sm:w-[min(520px,100%)]",
  lg: "sm:w-[min(680px,100%)]",
};

interface DialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: ReactNode;
  /** Focus a specific control on open; by default Radix focuses the first focusable one. */
  initialFocus?: () => void;
  /** Actions pinned under the scrolling body, such as Cancel and Save. */
  footer?: ReactNode;
  /** "md" is 520px wide, "lg" 680px. Phones always get the full width. */
  size?: DialogSize;
  children?: ReactNode;
}

/**
 * A modal window. Long content scrolls inside it while the title and footer stay put. On
 * phones it becomes a bottom sheet; from `sm` up it sits near the top of the screen and moves
 * up only as far as its height needs.
 */
export function Dialog({
  open,
  onOpenChange,
  title,
  description,
  initialFocus,
  footer,
  size = "md",
  children,
}: DialogProps) {
  const t = useT();
  const hasBody = children !== undefined && children !== null && children !== false;
  return (
    <RadixDialog.Root open={open} onOpenChange={onOpenChange}>
      <RadixDialog.Portal>
        {/* The overlay lays the window out: a bottom sheet on phones, otherwise a column whose
            top spacer gives way when the window is tall. */}
        <RadixDialog.Overlay
          className={cn(
            LAYER.dialog,
            "fixed inset-0 flex animate-fade-in flex-col items-center justify-end bg-scrim sm:justify-start sm:px-4 sm:py-8",
          )}
        >
          <div aria-hidden="true" className="hidden shrink basis-[calc(12vh-2rem)] sm:block" />
          <RadixDialog.Content
            // Without a description there is nothing to point at (Radix warns otherwise).
            {...(description ? {} : { "aria-describedby": undefined })}
            onOpenAutoFocus={(event) => {
              if (initialFocus) {
                event.preventDefault();
                initialFocus();
              }
            }}
            className={cn(
              "flex max-h-[90dvh] w-full shrink-0 animate-sheet-up flex-col overflow-hidden rounded-t-xl border border-b-0 border-line bg-surface pb-[env(safe-area-inset-bottom)] shadow-overlay focus:outline-none",
              "sm:max-h-[calc(100dvh-4rem)] sm:animate-pop-in sm:rounded-xl sm:border-b sm:pb-0",
              WIDTH[size],
            )}
          >
            <div
              className={cn(
                "flex shrink-0 items-start justify-between gap-4 px-5 pt-5",
                !hasBody && "pb-5",
              )}
            >
              <div className="min-w-0">
                <RadixDialog.Title className="text-[17px] font-semibold text-ink">
                  {title}
                </RadixDialog.Title>
                {description ? (
                  <RadixDialog.Description className="mt-1 text-[14px] leading-relaxed text-ink-muted">
                    {description}
                  </RadixDialog.Description>
                ) : null}
              </div>
              <RadixDialog.Close
                className="-me-1.5 -mt-1 grid size-7 shrink-0 place-items-center rounded-md text-ink-subtle transition-colors hover:bg-surface-hover hover:text-ink"
                aria-label={t("common.close")}
              >
                <X className="size-4" aria-hidden="true" />
              </RadixDialog.Close>
            </div>
            {hasBody ? (
              <div className="min-h-0 flex-1 overflow-y-auto px-5 pt-4 pb-5">{children}</div>
            ) : null}
            {footer ? (
              <div className="flex shrink-0 flex-wrap items-center justify-end gap-2 border-t border-line px-5 py-3.5">
                {footer}
              </div>
            ) : null}
          </RadixDialog.Content>
        </RadixDialog.Overlay>
      </RadixDialog.Portal>
    </RadixDialog.Root>
  );
}
